import { useTranslation } from 'react-i18next';
import { useState, useMemo } from 'react';
import {
  Box,
  Text,
  Flex,
  Spinner,
  Center,
  Field,
  PopoverRoot,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
} from '@chakra-ui/react';
import { DrawerComponent } from '../../ui/drawerComponent';
import {
  Button as ButtonComponent,
  InputField,
  ToggleButton,
} from '@themes/aegix';
import { RiCloseCircleLine } from 'react-icons/ri';
import { HiPlus } from 'react-icons/hi2';
import { FiInfo } from 'react-icons/fi';
import {
  useThreads,
  useThreadParticipants,
} from '../../../hooks/queries/useThreads';
import type { Thread, ThreadParticipant } from '../../../services';

export interface GroupSelection {
  id: number;
  name: string;
}

/**
 * Format a participant for the member-peek popover. Mirrors
 * AIM's `getMemberDisplayName` in `AlertTypeForm.tsx` so the two
 * surfaces show the same string for the same user.
 */
function getParticipantDisplayName(member: ThreadParticipant): string {
  if (member.name) return member.name;
  const composed =
    `${member.first_name ?? ''} ${member.last_name ?? ''}`.trim();
  if (composed) return composed;
  if (member.email) return member.email;
  return 'Unknown';
}

/**
 * Per-row member-peek. Click the `(i)` icon to open a
 * popover; the participants query fires lazily on first open and is
 * cached per-thread for the React-Query default window.
 *
 * UX mirrors AIM Settings > Types > Edit (`AlertTypeForm` +
 * `GroupsSection`) but uses click-trigger (Popover) rather than
 * hover-trigger (Tooltip) because the popover lists members; users
 * should be able to read it without keeping the cursor stationary.
 */
function GroupMembersPopover({
  threadId,
  groupName,
}: {
  threadId: number;
  groupName: string;
}) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, isError } = useThreadParticipants(threadId, open);
  const members = data?.results ?? [];

  return (
    <PopoverRoot
      open={open}
      onOpenChange={(d) => setOpen(d.open)}
      positioning={{ placement: 'right-start' }}
    >
      <PopoverTrigger asChild>
        <Box
          as="button"
          aria-label={`Show members of ${groupName}`}
          color="text.tertiary"
          _hover={{ color: 'text.secondary' }}
          cursor="pointer"
          display="inline-flex"
          alignItems="center"
          ml={2}
          data-e2e={`group-members-info-${threadId}`}
        >
          <FiInfo size={14} />
        </Box>
      </PopoverTrigger>
      <PopoverContent width="240px">
        <PopoverArrow />
        <PopoverBody p={3}>
          <Text fontSize="xs" fontWeight={600} mb={2} color="text.primary">
            Members
          </Text>
          {isLoading ? (
            <Center py={2}>
              <Spinner size="xs" />
            </Center>
          ) : isError ? (
            <Text fontSize="xs" color="text.danger">
              Failed to load members.
            </Text>
          ) : members.length === 0 ? (
            <Text fontSize="xs" color="text.secondary">
              No members.
            </Text>
          ) : (
            <Flex direction="column" gap={1}>
              {members.map((m) => (
                <Text key={m.id} fontSize="xs" color="text.primary">
                  {getParticipantDisplayName(m)}
                </Text>
              ))}
            </Flex>
          )}
        </PopoverBody>
      </PopoverContent>
    </PopoverRoot>
  );
}

interface ProtocolGroupSelectorProps {
  schoolId: number | null;
  selectedGroups: GroupSelection[];
  onGroupsChange: (groups: GroupSelection[]) => void;
  /**
   * The two protocol-only toggles are optional so the picker can
   * be reused as a plain site-scoped chat-group selector (drill schedule
   * notify groups). Omit the handlers to hide the toggle rows.
   */
  showAll?: boolean;
  onShowAllChange?: (value: boolean) => void;
  elevateMembers?: boolean;
  onElevateMembersChange?: (value: boolean) => void;
  /** Copy shown when nothing is selected (semantics differ per caller). */
  emptyStateText?: string;
}

/**
 * Picker for the `alert_to_specific_groups_only` value on an AlertType.
 *
 * Wire shape on the AlertType: `alert_to_specific_groups_only: number[]`
 * — a list of `chat.Thread.id` values. The picker exposes the chat
 * groups configured at the active site so an SMS author can scope a
 * protocol's notifications to a subset of chat-group members instead
 * of "all users at site".
 *
 * Data source: `useThreads` → `/api/v2/thread/`, filtered to
 * `thread_type === 'group'` AND `incident_id == null`. The wire field
 * accepts any `Thread.id`, but only admin-created multi-user chat groups
 * make product sense here: alert chats, drill chats, direct messages and
 * reunification threads are excluded, and so are per-incident temporary
 * chats, which the serializer also tags as `thread_type === 'group'`
 * (`incident_id` set, `alert_id` null). Customer-facing labels say
 * "Group" / "Notify Group" to match the data. The data source is NOT
 * `/api/v2/roles/` — roles do not match the wire-field semantics.
 *
 * Show all / Elevate are themed `ToggleButton`s, and each selected group
 * row carries a click-triggered `(i)` member-peek popover (mirroring
 * AIM's Settings > Types > Edit).
 *
 * `schoolId` matters: `useThreads` filters by `school` so the chat-group
 * catalogue is correctly scoped per-site.
 *
 * This component is part of the **legacy "Notify"** path. Per ADR 116
 * the new "Recipients" editor will supersede it once AAS-1143's Phase
 * 6/7 cutover fires — but until that lands the legacy section is
 * still authoritative for delivery, so a confusing picker is a real
 * bug, not a cosmetic one.
 */
export function ProtocolGroupSelector({
  schoolId,
  selectedGroups,
  onGroupsChange,
  showAll = false,
  onShowAllChange,
  elevateMembers = false,
  onElevateMembersChange,
  emptyStateText,
}: ProtocolGroupSelectorProps) {
  const { t } = useTranslation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const defaultEmptyState = t(
    'protocols.noGroupsSelected',
    'No groups selected — all users at site will be notified.',
  );
  const resolvedEmptyStateText = emptyStateText ?? defaultEmptyState;

  const { data: threadsData, isLoading } = useThreads({
    school: schoolId ?? undefined,
    page_size: 200,
  });

  const availableGroups = useMemo(() => {
    if (!threadsData?.results) return [];
    const selectedIds = new Set(selectedGroups.map((g) => g.id));
    // `thread_type === 'group'` alone is not enough — the
    // serializer's else branch (no `alert_id`, no `reunification_event_id`)
    // tags incident-scoped threads as `'group'` too. Filter on
    // `incident_id == null` to exclude per-incident temporary chats,
    // which are not selectable as default recipients on a protocol.
    let filtered = threadsData.results.filter(
      (t: Thread) =>
        t.thread_type === 'group' &&
        t.incident_id == null &&
        !selectedIds.has(t.id),
    );
    if (searchValue) {
      const lower = searchValue.toLowerCase();
      filtered = filtered.filter((t: Thread) =>
        t.name.toLowerCase().includes(lower),
      );
    }
    return filtered;
  }, [threadsData, selectedGroups, searchValue]);

  const handleSelectGroup = (thread: Thread) => {
    onGroupsChange([...selectedGroups, { id: thread.id, name: thread.name }]);
  };

  const handleRemoveGroup = (groupId: number) => {
    onGroupsChange(selectedGroups.filter((g) => g.id !== groupId));
  };

  return (
    <Box>
      {selectedGroups.length === 0 ? (
        <Text fontSize="sm" color="text.secondary" mb={3}>
          {resolvedEmptyStateText}
        </Text>
      ) : (
        <Box mb={3}>
          {selectedGroups.map((group) => (
            <Flex
              key={group.id}
              alignItems="center"
              justifyContent="space-between"
              px={3}
              py={2}
              mb={1}
              bg="surface.secondary"
              borderRadius="md"
            >
              <Flex alignItems="center">
                <Text fontSize="sm" color="text.primary">
                  {group.name}
                </Text>
                <GroupMembersPopover
                  threadId={group.id}
                  groupName={group.name}
                />
              </Flex>
              <Box
                as="button"
                color="text.danger"
                _hover={{ color: 'red.600' }}
                onClick={() => handleRemoveGroup(group.id)}
              >
                <RiCloseCircleLine size={18} />
              </Box>
            </Flex>
          ))}
        </Box>
      )}

      <ButtonComponent
        visual="secondary"
        size="sm"
        onClick={() => {
          setSearchValue('');
          setIsDrawerOpen(true);
        }}
        data-e2e="add-group-button"
      >
        <HiPlus size={14} />
        <Text ml={1}>{t('protocols.addGroup', 'Add Group')}</Text>
      </ButtonComponent>

      {selectedGroups.length > 0 &&
        onShowAllChange &&
        onElevateMembersChange && (
          <Box mt={4}>
            <Flex direction="column" gap={3}>
              <ToggleButton
                title={t(
                  'protocols.showTypeToAll',
                  'Show type to all users at site',
                )}
                checked={showAll}
                onCheckedChange={() => onShowAllChange(!showAll)}
                e2eTitle="show-all-users-toggle"
              />
              <ToggleButton
                title={t(
                  'protocols.elevateAudience',
                  'Elevate audience members',
                )}
                checked={elevateMembers}
                onCheckedChange={() => onElevateMembersChange(!elevateMembers)}
                e2eTitle="elevate-audience-toggle"
              />
            </Flex>
          </Box>
        )}

      <DrawerComponent
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={t('protocols.selectGroup', 'Select Group')}
        size="sm"
        e2eTitle="select-group-drawer"
      >
        <Box>
          <Field.Root mb={3}>
            <Field.Label htmlFor="group-search" srOnly>
              {t('protocols.searchGroups', 'Search groups')}
            </Field.Label>
            <InputField
              id="group-search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder={t(
                'protocols.searchGroupsPlaceholder',
                'Search groups...',
              )}
              size="sm"
              data-e2e="group-search-input"
            />
          </Field.Root>

          {isLoading ? (
            <Center py={6}>
              <Spinner size="sm" />
            </Center>
          ) : availableGroups.length === 0 ? (
            <Text
              fontSize="sm"
              color="text.secondary"
              textAlign="center"
              py={4}
            >
              {t('protocols.noGroupsAvailable', 'No groups available')}
            </Text>
          ) : (
            <Box>
              {availableGroups.map((thread) => (
                <Flex
                  key={thread.id}
                  alignItems="center"
                  justifyContent="space-between"
                  px={3}
                  py={2}
                  mb={1}
                  cursor="pointer"
                  borderRadius="md"
                  _hover={{ bg: 'surface.tertiary' }}
                  onClick={() => {
                    handleSelectGroup(thread);
                    setIsDrawerOpen(false);
                  }}
                  data-e2e={`select-group-row-${thread.id}`}
                >
                  <Text fontSize="sm" color="text.primary">
                    {thread.name}
                  </Text>
                  {thread.members_count !== undefined && (
                    <Text fontSize="xs" color="text.secondary">
                      {thread.members_count} {t('protocols.members', 'members')}
                    </Text>
                  )}
                </Flex>
              ))}
            </Box>
          )}
        </Box>
      </DrawerComponent>
    </Box>
  );
}
